/* ============================================================
   PEMBELIAN SAYA  (/marketplace/pembelian)  —  v3.9
   ============================================================
   Pasangan rupa v3.9 bagi MyPurchasesView.jsx. Route memilih antara
   kedua-duanya ikut suis `config/ui_v39` (lihat App.jsx). Halaman
   lama TIDAK dipadam.

   ⛔ LOGIK DIPINDAH BULAT-BULAT. Halaman ini ialah tempat pelanggan
      mengambil semula barang yang sudah dibayar — muat turun ebook
      dan info akses akaun. Urutan pemuatan, pengendalian ralat dan
      mesej semuanya kekal sama seperti halaman lama.

   ⚠️ Info akses dimuat AWAL untuk pesanan `delivered` yang bukan
      ebook — satu bacaan setiap pesanan, dijalankan serentak.
      Kegagalan satu pesanan sengaja DIDIAMKAN (rules menolak kalau
      bukan pemilik); kad itu akan papar "Tiada info dipaparkan"
      dan bukannya merosakkan seluruh senarai.

   ⚠️ Halaman ini TIADA versi prototaip -v3.9 — ia hidup sahaja.
   ============================================================ */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    CheckCircle2, Clock, Copy, CreditCard, Download, Hourglass,
    Key, Store, Undo2, XCircle,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, Card, EmptyState, PageHeader } from '../design/ui';
import { useT } from '../i18n';
import { IKON_KATEGORI, IKON_LAIN } from './marketplaceIcons';
import {
    listMyOrders, getOrderDelivery, getMarketplaceDownloadUrl, getMarketplaceAccess, barisPesanan,
    formatPrice, categoryLabel,
} from '../services/marketplaceService';
import { toast } from './toastBus';

/* Tarikh tamat akses. Order membawa Timestamp Firestore; callable pula
   memulangkan milisaat — kedua-duanya diterima di sini. */
function tarikhPenuh(ts) {
    const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* Nota: pembantu `sudahTamat` dibuang bersama kerja troli. Tarikh tamat kini
   datang PER AKAUN daripada server (ia dibaca terus daripada akaun itu sendiri),
   jadi tekaan sisi pelayar tidak lagi diperlukan — dan ia memang pernah tersasar
   selepas pembeli menyambung sewa. */

/* Teks status kekal sama seperti halaman lama — pelanggan yang sudah
   biasa dengannya tidak patut terpaksa belajar semula perkataan baharu.
   Warna pula datang daripada nada Badge, bukan kod warna sendiri.

   Pemalar aras-modul tidak boleh memanggil useT(), jadi ia menyimpan
   KUNCI kamus; terjemahan berlaku pada masa render di bawah. */
const STATUS = {
    awaiting_payment: { kunci: 'purchases.status.awaitingPayment', tone: 'warn', Ikon: CreditCard },
    requested: { kunci: 'purchases.status.requested', tone: 'warn', Ikon: Hourglass },
    delivered: { kunci: 'purchases.status.delivered', tone: 'ok', Ikon: CheckCircle2 },
    // Troli: sebahagian barang sudah diserah, bakinya menunggu admin menambah stok.
    partial: { kunci: 'purchases.status.partial', tone: 'warn', Ikon: Hourglass },
    rejected: { kunci: 'purchases.status.rejected', tone: 'stop', Ikon: XCircle },
    refunded: { kunci: 'purchases.status.refunded', tone: 'neutral', Ikon: Undo2 },
};

export default function MyPurchasesV39View() {
    const navigate = useNavigate();
    const t = useT();
    const [searchParams] = useSearchParams();
    const orderPulang = searchParams.get('order'); // pulang daripada Billplz
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deliveries, setDeliveries] = useState({}); // orderId -> teks info
    const [downloading, setDownloading] = useState(null);
    const [access, setAccess] = useState({});         // orderId -> { content, expiresAt, expired }
    const [loadingAccess, setLoadingAccess] = useState(null);

    useEffect(() => { load(); }, []);

    /* Pulang daripada Billplz: webhook mungkin belum sempat mendarat (biasanya
       beberapa saat). Tanpa semakan berulang, pelanggan yang baru sahaja bayar
       melihat "Menunggu pembayaran" dan menyangka duitnya hilang. */
    useEffect(() => {
        if (!orderPulang) return undefined;
        let cubaan = 0;
        const jam = setInterval(async () => {
            cubaan += 1;
            const list = await load(true);
            const o = list?.find((x) => x.id === orderPulang);
            if (!o || o.status !== 'awaiting_payment' || cubaan >= 10) clearInterval(jam);
        }, 3000);
        return () => clearInterval(jam);
    }, [orderPulang]);

    async function load(senyap = false) {
        if (!senyap) setLoading(true);
        try {
            const list = await listMyOrders();
            setOrders(list);
            // Pesanan auto-serah SENGAJA dikecualikan — kredentialnya tidak duduk dalam
            // doc ini, dan ia hanya diambil bila pelanggan menekan "Lihat Akses"
            // (rahsia tidak dipaparkan tanpa dipinta).
            // Pesanan troli tiada `category`/`fulfillment` tunggal — ia dinilai
            // daripada barisnya. Auto-serah dan ebook SENGAJA dikecualikan:
            // kredential/fail tidak pernah duduk dalam doc ini.
            const manual = list.filter((o) => {
                if (o.status !== 'delivered' && o.status !== 'partial') return false;
                const b = barisPesanan(o);
                return !b.some((x) => x.category === 'ebook' || x.fulfillment === 'auto_stock');
            });
            const map = {};
            await Promise.all(manual.map(async (o) => {
                try {
                    const d = await getOrderDelivery(o.id);
                    if (d?.info) map[o.id] = d.info;
                } catch { /* abai — rules menolak kalau bukan pemilik */ }
            }));
            setDeliveries(map);
            return list;
        } catch (e) {
            // Semakan senyap tidak menjerit — ia berjalan sendiri di latar,
            // dan toast berulang setiap 3 saat lebih mengganggu daripada membantu.
            if (!senyap) toast.error(t('purchases.loadFailed', { ralat: e?.message || e }));
            return null;
        } finally {
            if (!senyap) setLoading(false);
        }
    }

    async function handleDownload(o, lineIndex) {
        setDownloading(o.id);
        try {
            const { url } = await getMarketplaceDownloadUrl(o.id, lineIndex);
            window.open(url, '_blank');
        } catch (e) {
            toast.error(t('purchases.downloadFailed', { ralat: e?.message || e }));
        } finally {
            setDownloading(null);
        }
    }

    /* Kredential diminta daripada server pada klik — bukan dimuat awal.
       Server yang menyemak pemilik pesanan dan tempoh akses. */
    async function handleLihatAkses(o) {
        setLoadingAccess(o.id);
        try {
            const res = await getMarketplaceAccess(o.id);
            setAccess((m) => ({ ...m, [o.id]: res }));
        } catch (e) {
            toast.error(t('purchases.accessFailed', { ralat: e?.message || e }));
        } finally {
            setLoadingAccess(null);
        }
    }

    async function handleCopy(text) {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(t('purchases.copied'));
        } catch {
            toast.error(t('purchases.copyFailed'));
        }
    }

    return (
        <>
            {/* Penuh lebar — bentuk lalai v3.9 untuk halaman senarai. */}
            <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

                <PageHeader
                    title={t('purchases.title')}
                    subtitle={t('purchases.subtitle')}
                    actions={
                        <Btn variant="ghost" icon={<Store size={15} strokeWidth={2} />}
                            onClick={() => navigate('/marketplace')}>
                            {t('purchases.marketplace')}
                        </Btn>
                    }
                />

                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="s-skel" style={{
                                height: 104, borderRadius: 'var(--s-r-lg)',
                                border: '1px solid var(--s-line)', background: 'var(--s-card)',
                            }} />
                        ))}
                    </div>
                ) : orders.length === 0 ? (
                    <EmptyState
                        title={t('purchases.empty')}
                        action={
                            <Btn icon={<Store size={15} strokeWidth={2} />} onClick={() => navigate('/marketplace')}>
                                {t('purchases.toMarketplace')}
                            </Btn>
                        }
                    >
                        {t('purchases.emptyDesc')}
                    </EmptyState>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {orders.map((o) => {
                            const st = STATUS[o.status] || STATUS.requested;
                            // Pesanan troli membawa banyak barang; pesanan lama satu.
                            // SEMUA keputusan paparan di bawah dibuat daripada BARIS,
                            // bukan medan aras-atas yang hanya wujud pada pesanan lama.
                            const baris = barisPesanan(o);
                            const Ikon = IKON_KATEGORI[baris[0]?.category] || IKON_LAIN;
                            const adaEbook = baris.some((b) => b.category === 'ebook');
                            const adaAkaun = baris.some((b) => b.fulfillment === 'auto_stock');
                            const banyakBarang = baris.length > 1 || baris.some((b) => b.qty > 1);
                            const kurang = Number(o.shortfallTotal) || 0;
                            // 'partial' dikira SUDAH diserah — pembeli memang sudah memegang
                            // sebahagian barangnya, dan menyorokkannya bermakna dia membayar
                            // tetapi tidak nampak apa yang dia sudah dapat.
                            const delivered = o.status === 'delivered' || o.status === 'partial';
                            const akses = access[o.id];
                            /* Callable memulangkan SENARAI akaun (bentuk troli). Bentuk LAMA
                               (satu `content`) dibungkus jadi senarai satu, supaya paparan
                               di bawah hanya ada SATU laluan dan bukan dua. */
                            const senaraiAkaun = akses?.akaun?.length
                                ? akses.akaun
                                : ((akses?.content || akses?.expired)
                                    ? [{ content: akses.content, expired: akses.expired, expiresAt: akses.expiresAt }]
                                    : []);

                            return (
                                <Card key={o.id} style={{ padding: 18, display: 'flex', gap: 14, alignItems: 'flex-start' }}>

                                    <div style={{
                                        width: 42, height: 42, borderRadius: 'var(--s-r)', flexShrink: 0,
                                        display: 'grid', placeItems: 'center',
                                        background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                                    }}>
                                        <Ikon size={19} strokeWidth={1.7} style={{ color: 'var(--s-ink-2)' }} />
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--s-ink)', lineHeight: 1.35 }}>
                                                    {o.productTitle}
                                                </div>
                                                <div className="s-hint" style={{ marginTop: 3 }}>
                                                    {categoryLabel(baris[0]?.category)} · {o.amount ? formatPrice(o.amount) : t('purchases.free')}
                                                </div>
                                                {/* Senarai barang muncul hanya bila ia memberitahu
                                                    sesuatu yang BAHARU — pesanan satu barang satu
                                                    unit sudah pun dinamakan pada tajuk di atas. */}
                                                {banyakBarang && (
                                                    <ul style={{
                                                        margin: '8px 0 0', padding: 0, listStyle: 'none',
                                                        display: 'flex', flexDirection: 'column', gap: 3,
                                                    }}>
                                                        {baris.map((b) => (
                                                            <li key={b.idx} className="s-hint">
                                                                {b.qty} × {b.productTitle}
                                                                {b.shortfall > 0 && ` — ${t('purchases.linePending', { n: b.shortfall })}`}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                            <Badge tone={st.tone} style={{ height: 'fit-content' }}>
                                                <st.Ikon size={12} strokeWidth={2} />{t(st.kunci)}
                                            </Badge>
                                        </div>

                                        {delivered && adaEbook && (
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                                                {baris.filter((b) => b.category === 'ebook').map((b) => (
                                                    <Btn
                                                        key={b.idx}
                                                        size="sm"
                                                        disabled={downloading === o.id}
                                                        onClick={() => handleDownload(o, b.idx)}
                                                        icon={<Download size={14} strokeWidth={2} />}
                                                    >
                                                        {downloading === o.id
                                                            ? t('purchases.preparing')
                                                            : (baris.length > 1 ? b.productTitle : t('purchases.downloadEbook'))}
                                                    </Btn>
                                                ))}
                                            </div>
                                        )}

                                        {/* ─── Akaun auto-serah ───
                                            Satu pesanan troli boleh membawa BANYAK akaun.
                                            Setiap satu dipapar dalam kotaknya sendiri dengan
                                            butang salin sendiri — satu blok teks gergasi yang
                                            digabungkan mustahil hendak dibaca, dan pelanggan
                                            perlu menyalin satu akaun pada satu masa semasa dia
                                            log masuk ke TikTok. */}
                                        {delivered && adaAkaun && (
                                            <div style={{
                                                marginTop: 12, padding: 14, borderRadius: 'var(--s-r)',
                                                border: '1px solid var(--s-line)', background: 'var(--s-ground)',
                                            }}>
                                                <div style={{
                                                    display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', gap: 10, marginBottom: 8,
                                                }}>
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                                        fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em',
                                                        textTransform: 'uppercase', color: 'var(--s-ink-3)',
                                                    }}>
                                                        <Key size={12} strokeWidth={2} />
                                                        {t('purchases.accountAccess')}
                                                        {senaraiAkaun.length > 1 && ` · ${senaraiAkaun.length}`}
                                                    </span>
                                                    {senaraiAkaun.filter((a) => a.content).length > 1 && (
                                                        <Btn variant="ghost" size="sm"
                                                            icon={<Copy size={13} strokeWidth={2} />}
                                                            onClick={() => handleCopy(senaraiAkaun
                                                                .filter((a) => a.content)
                                                                .map((a) => a.content).join('\n\n———\n\n'))}>
                                                            {t('purchases.copyAll')}
                                                        </Btn>
                                                    )}
                                                </div>

                                                {!akses ? (
                                                    <Btn size="sm" disabled={loadingAccess === o.id}
                                                        icon={<Key size={14} strokeWidth={2} />}
                                                        onClick={() => handleLihatAkses(o)}>
                                                        {loadingAccess === o.id ? t('purchases.opening') : t('purchases.viewAccess')}
                                                    </Btn>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                        {senaraiAkaun.map((a, i) => (
                                                            <div key={a.itemId || i} style={{
                                                                padding: senaraiAkaun.length > 1 ? '10px 12px' : 0,
                                                                borderRadius: senaraiAkaun.length > 1 ? 10 : 0,
                                                                border: senaraiAkaun.length > 1 ? '1px solid var(--s-line)' : 'none',
                                                                background: senaraiAkaun.length > 1 ? 'var(--s-card)' : 'transparent',
                                                            }}>
                                                                {senaraiAkaun.length > 1 && (
                                                                    <div style={{
                                                                        fontSize: 12, fontWeight: 600,
                                                                        color: 'var(--s-ink-2)', marginBottom: 6,
                                                                    }}>
                                                                        {i + 1}. {a.productTitle || o.productTitle}
                                                                    </div>
                                                                )}

                                                                {a.expiresAt && (
                                                                    <div className="s-hint" style={{ margin: '0 0 8px' }}>
                                                                        {a.expired
                                                                            ? t('purchases.expiredOn', { tarikh: tarikhPenuh(a.expiresAt) })
                                                                            : t('purchases.activeUntil', { tarikh: tarikhPenuh(a.expiresAt) })}
                                                                    </div>
                                                                )}

                                                                {a.expired ? (
                                                                    <div className="s-hint" style={{ margin: 0 }}>
                                                                        {t('purchases.expiredNote')}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                                                        <pre style={{
                                                                            margin: 0, flex: 1, minWidth: 0,
                                                                            fontFamily: 'var(--s-mono)', fontSize: 13,
                                                                            color: 'var(--s-ink)', whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word', lineHeight: 1.6,
                                                                        }}>
                                                                            {a.content}
                                                                        </pre>
                                                                        <Btn variant="ghost" size="sm"
                                                                            icon={<Copy size={13} strokeWidth={2} />}
                                                                            onClick={() => handleCopy(a.content)}>
                                                                            {t('purchases.copy')}
                                                                        </Btn>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}

                                                        {senaraiAkaun.length > 0 && senaraiAkaun.every((a) => a.expired) && (
                                                            <Btn size="sm" style={{ alignSelf: 'flex-start' }}
                                                                onClick={() => navigate(`/marketplace/p/${baris[0]?.productId || o.productId}`)}>
                                                                {t('purchases.renew')}
                                                            </Btn>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {delivered && !adaEbook && !adaAkaun && (
                                            <div style={{
                                                marginTop: 12, padding: 14, borderRadius: 'var(--s-r)',
                                                border: '1px solid var(--s-line)', background: 'var(--s-ground)',
                                            }}>
                                                <div style={{
                                                    display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', gap: 10, marginBottom: 8,
                                                }}>
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                                        fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em',
                                                        textTransform: 'uppercase', color: 'var(--s-ink-3)',
                                                    }}>
                                                        <Key size={12} strokeWidth={2} />{t('purchases.accessInfo')}
                                                    </span>
                                                    {deliveries[o.id] && (
                                                        <Btn variant="ghost" size="sm"
                                                            icon={<Copy size={13} strokeWidth={2} />}
                                                            onClick={() => handleCopy(deliveries[o.id])}>
                                                            {t('purchases.copy')}
                                                        </Btn>
                                                    )}
                                                </div>
                                                {deliveries[o.id] ? (
                                                    <pre style={{
                                                        margin: 0, fontFamily: 'var(--s-mono)', fontSize: 13,
                                                        color: 'var(--s-ink)', whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word', lineHeight: 1.6,
                                                    }}>
                                                        {deliveries[o.id]}
                                                    </pre>
                                                ) : (
                                                    <div className="s-hint" style={{ margin: 0 }}>
                                                        {t('purchases.noInfo')}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {kurang > 0 && (
                                            <div style={{
                                                display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12,
                                                padding: '10px 12px', borderRadius: 'var(--s-r)',
                                                background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                                            }}>
                                                <Hourglass size={14} strokeWidth={2} style={{ color: 'var(--s-warn)', flexShrink: 0, marginTop: 2 }} />
                                                <span className="s-hint">{t('purchases.shortfallNote', { n: kurang })}</span>
                                            </div>
                                        )}

                                        {o.status === 'awaiting_payment' && (
                                            <div style={{ marginTop: 12 }}>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: 7,
                                                    fontSize: 12.5, color: 'var(--s-warn)',
                                                    background: 'var(--s-warn-soft)', border: '1px solid var(--s-line)',
                                                    borderRadius: 'var(--s-r)', padding: '9px 12px',
                                                }}>
                                                    <Clock size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
                                                    {o.id === orderPulang
                                                        ? t('purchases.payVerifying')
                                                        : t('purchases.payIncomplete')}
                                                </div>
                                                {o.billplzUrl && (
                                                    <Btn size="sm" style={{ marginTop: 10 }}
                                                        icon={<CreditCard size={14} strokeWidth={2} />}
                                                        onClick={() => { window.location.href = o.billplzUrl; }}>
                                                        {t('purchases.payNow')}
                                                    </Btn>
                                                )}
                                            </div>
                                        )}

                                        {o.status === 'requested' && (
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: 7, marginTop: 12,
                                                fontSize: 12.5, color: 'var(--s-warn)',
                                                background: 'var(--s-warn-soft)', border: '1px solid var(--s-line)',
                                                borderRadius: 'var(--s-r)', padding: '9px 12px',
                                            }}>
                                                <Clock size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
                                                {t('purchases.requestedNote')}
                                            </div>
                                        )}

                                        {o.status === 'rejected' && o.rejectReason && (
                                            <div className="s-hint" style={{ marginTop: 8 }}>
                                                {t('purchases.rejectReason', { sebab: o.rejectReason })}
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
